// A direction is "<from>to<to>" built from 3-letter script codes
// ("lattocyr", "cyrtonew", ...). The backend splits the mod string the same way.

import type { ShareLanguage } from "~/services/share";

export type Script = "latin" | "cyrillic" | "newLatin";

export const SCRIPTS: Script[] = ["latin", "cyrillic", "newLatin"];

// i18n keys for the script names shown in the direction selects.
export const SCRIPT_LABEL_KEY: Record<Script, string> = {
  latin: "Latin",
  cyrillic: "Cyrillic",
  newLatin: "New Latin",
};

const CODE: Record<Script, string> = {
  latin: "lat",
  cyrillic: "cyr",
  newLatin: "new",
};

// Language names stored on shared translations (backend column is 10 chars).
const SHARE_LANG: Record<Script, ShareLanguage> = {
  latin: "lotin",
  cyrillic: "kirill",
  newLatin: "yangilotin",
};

export const DEFAULT_DIRECTION = "lattocyr";

export const ALL_DIRECTIONS = SCRIPTS.flatMap((from) =>
  SCRIPTS.filter((to) => to !== from).map((to) => `${CODE[from]}to${CODE[to]}`),
);

export function isScript(value: unknown): value is Script {
  return typeof value === "string" && (SCRIPTS as string[]).includes(value);
}

export function buildDirection(from: Script, to: Script): string {
  return `${CODE[from]}to${CODE[to]}`;
}

function tryParse(direction: string | null | undefined) {
  if (!direction || direction.length !== 8 || direction.slice(3, 5) !== "to") {
    return null;
  }
  const from = SCRIPTS.find((s) => CODE[s] === direction.slice(0, 3));
  const to = SCRIPTS.find((s) => CODE[s] === direction.slice(5));
  return from && to && from !== to ? { from, to } : null;
}

export function isDirection(value: string | null | undefined): value is string {
  return tryParse(value) !== null;
}

export function parseDirection(direction: string): { from: Script; to: Script } {
  return tryParse(direction) ?? { from: "latin", to: "cyrillic" };
}

// Picking the script already on the other side swaps the two sides.
export function withFrom(direction: string, from: Script): string {
  const current = parseDirection(direction);
  return buildDirection(from, current.to === from ? current.from : current.to);
}

export function withTo(direction: string, to: Script): string {
  const current = parseDirection(direction);
  return buildDirection(current.from === to ? current.to : current.from, to);
}

export function swapDirection(direction: string): string {
  const { from, to } = parseDirection(direction);
  return buildDirection(to, from);
}

// detectLanguage only tells Cyrillic from Latin-looking text, so it corrects
// the source side only and keeps a chosen New Latin side.
export function applyDetectedSource(
  direction: string,
  detected: "lattocyr" | "cyrtolat",
): string {
  const { from } = parseDirection(direction);
  if (detected === "cyrtolat") {
    return from === "cyrillic" ? direction : withFrom(direction, "cyrillic");
  }
  return from === "cyrillic" ? withFrom(direction, "latin") : direction;
}

export function shareLanguages(direction: string): {
  from: ShareLanguage;
  to: ShareLanguage;
} {
  const { from, to } = parseDirection(direction);
  return { from: SHARE_LANG[from], to: SHARE_LANG[to] };
}

export function directionFromShare(fromLang: string, toLang: string): string {
  const from = SCRIPTS.find((s) => SHARE_LANG[s] === fromLang);
  const to = SCRIPTS.find((s) => SHARE_LANG[s] === toLang);
  return from && to && from !== to ? buildDirection(from, to) : DEFAULT_DIRECTION;
}
